import styled from 'styled-components';

import SharedText from '../text';
import { Colors } from 'src/utils/color';

// Global font family for styled components
const fontFamily = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif";

export const Wrapper = styled.div`
    width: 100%;
    height: 100vh;
`;

export const ContentWrapper = styled.div`
    width: 100%;
    min-height: 100vh;
    display: flex;
    position: relative;
    overflow: hidden;
`;

export const LeftBarContainer = styled.div`
    width: 64px;
    background: ${Colors.theme.page_background};
    display: flex;
    flex-direction: column;
    align-items: center;
    border-right: solid;
    border-color: ${Colors.extra.white};
`;

// New unified sidebar styles
export const UnifiedSidebar = styled.div<{ isOpen: boolean }>`
    width: ${props => props.isOpen ? '280px' : '64px'};
    height: 100vh;
    background-color: ${Colors.theme.sidebar_background};
    border-right: 1px solid ${Colors.theme.sidebar_border};
    display: flex;
    flex-direction: column;
    position: fixed;
    left: 0;
    top: 0;
    z-index: 1000;
    overflow: hidden;
    transition: width 0.3s ease;
    
    /* Ensure UserSection stays at bottom */
    & > *:last-child {
        margin-top: auto;
    }
`;

export const SidebarHeader = styled.div`
    padding: 20px 16px 16px 16px;
    border-bottom: 1px solid ${Colors.theme.sidebar_border};
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 80px;
    
    /* When sidebar is collapsed, center the toggle button */
    & > button:only-child {
        width: 100%;
        justify-content: center;
        margin: 0 auto;
    }
`;

export const ToggleButton = styled.button`
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    transition: background-color 0.2s ease;
    color: ${Colors.extra.black};
    
    &:hover {
        background-color: rgba(37, 125, 230, 0.1);
    }
    
    svg {
        width: 20px;
        height: 20px;
    }
`;

export const SidebarBrand = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

export const BrandIcon = styled.div`
    width: 40px;
    height: 40px;
    background: ${Colors.theme.primary};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${Colors.extra.white};
    font-weight: bold;
    font-size: 16px;
    font-family: ${fontFamily};
`;

export const BrandText = styled.div`
    display: flex;
    flex-direction: column;
`;

export const BrandTitle = styled(SharedText)`
    color: ${Colors.extra.black};
    font-size: 18px;
    font-weight: 600;
    margin: 0;
    font-family: ${fontFamily};
`;

export const BrandSubtitle = styled(SharedText)`
    color: ${Colors.extra.dark_gray};
    font-size: 12px;
    margin: 0;
    font-family: ${fontFamily};
`;

export const SidebarContent = styled.div`
    flex: 1;
    padding: 16px 0;
    overflow-y: auto;
`;

export const NavigationSection = styled.div`
    margin-bottom: 24px;
    width: 100%;
`;

export const SectionTitle = styled(SharedText)`
    color: ${Colors.extra.dark_gray};
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.5px;
    margin: 0 16px 12px 16px;
    font-family: ${fontFamily};
`;

export const NavigationItem = styled.div<{ active?: boolean; collapsed?: boolean }>`
    padding: ${props => props.collapsed ? '12px' : '12px 16px'};
    cursor: pointer;
    transition: all 0.2s ease;
    border-left: 3px solid transparent;
    background-color: ${props => props.active ? 'rgba(0, 0, 0, 0.05)' : 'transparent'};
    border-radius: 6px;
    margin: 2px 8px;
    display: flex;
    align-items: center;
    justify-content: ${props => props.collapsed ? 'center' : 'flex-start'};
    min-height: 40px;
    
    &:hover {
        background-color: rgba(0, 0, 0, 0.05);
    }
    
    &:active {
        background-color: rgba(0, 0, 0, 0.08);
    }
`;

export const NavigationText = styled(SharedText)`
    color: ${Colors.extra.black};
    font-size: 14px;
    font-weight: 500;
    margin: 0;
    font-family: ${fontFamily};
`;

export const UserSection = styled.div`
    padding: 16px;
    margin-top: auto;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    flex-shrink: 0;
`;

export const UserInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
    width: 100%;
`;

export const UserAvatar = styled.div`
    width: 40px;
    height: 40px;
    background: ${Colors.theme.primary};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${Colors.extra.white};
    font-weight: bold;
    font-size: 14px;
    font-family: ${fontFamily};
`;

export const UserDetails = styled.div`
    display: flex;
    flex-direction: column;
`;

export const UserName = styled(SharedText)`
    color: ${Colors.extra.black};
    font-size: 14px;
    font-weight: 600;
    margin: 0;
    font-family: ${fontFamily};
`;

export const UserRole = styled(SharedText)`
    color: ${Colors.extra.dark_gray};
    font-size: 12px;
    margin: 0;
    font-family: ${fontFamily};
`;

export const UserOrganization = styled(SharedText)`
    color: ${Colors.extra.dark_gray};
    font-size: 11px;
    margin: 4px 0 0 0;
    font-weight: 500;
    font-family: ${fontFamily};
`;

export const SignOutButton = styled.div`
    padding: 8px 16px;
    cursor: pointer;
    transition: all 0.2s ease;
    border-radius: 6px;
    
    /* Make SVG icons inherit the button's text color */
    svg {
        fill: currentColor;
        stroke: currentColor;
    }
    
    &:hover {
        background-color: ${Colors.extra.white}10;
    }
`;

export const SignOutText = styled(SharedText)`
    color: ${Colors.extra.dark_gray};
    font-size: 12px;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: ${fontFamily};
`;

export const Content = styled.div<{ sidebarOpen: boolean }>`
    flex: 1;
    padding: 0;
    background-color: ${Colors.theme.page_background};
    overflow-y: auto;
    overflow-x: hidden;
    margin-left: ${props => props.sidebarOpen ? '280px' : '64px'};
    width: calc(100vw - ${props => props.sidebarOpen ? '280px' : '64px'});
    height: 100vh;
    margin-top: 80px;
    position: relative;
    will-change: contents;
    transform: translateZ(0);
    backface-visibility: hidden;
    isolation: isolate;
    transition: margin-left 0.3s ease, width 0.3s ease;
`;

export const IconButton = styled.a`
    width: 64px;
    height: 64px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
`;

export const Topbar = styled.div<{ sidebarOpen: boolean }>`
    width: 100%;
    height: 80px;
    background: ${Colors.theme.page_background};
    border-bottom: 1px solid ${Colors.theme.sidebar_border};
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 24px;
    position: fixed;
    top: 0;
    left: ${props => props.sidebarOpen ? '280px' : '64px'};
    right: 0;
    z-index: 999;
    transition: left 0.3s ease;
`;

export const TopbarLeft = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

export const TopbarTitle = styled(SharedText)`
    font-size: 18px;
    font-weight: 700;
    color: ${Colors.extra.black};
    margin: 0;
    font-family: ${fontFamily};
`;

export const TopbarSubtitle = styled(SharedText)`
    font-size: 12px;
    color: ${Colors.extra.dark_gray};
    margin: 0;
    font-family: ${fontFamily};
`;

export const TopbarRight = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

export const ThemeToggleButton = styled.button`
    width: 40px;
    height: 40px;
    border-radius: 8px;
    border: 1px solid ${Colors.theme.sidebar_border};
    background: ${Colors.extra.white};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover {
        background: ${Colors.extra.light_gray};
    }
    
    svg {
        width: 20px;
        height: 20px;
        fill: currentColor;
        stroke: currentColor;
    }
`;

export const Icons = styled.img`
    width: 217px;
    height: 47px;
    margin-left: 8px;
    margin-right: 40px;
    cursor: pointer;
    transition: opacity 0.2s ease;
    
    &:hover {
        opacity: 0.8;
    }
`;

export const Text = styled(SharedText)`
    margin-right: 16px;
    cursor: pointer;
    font-size: 13px;
`;

export const RightNavWrapper = styled.div`
    height: 100%;
    margin-left: auto;
    display: flex;
    align-items: center;
`;

export const NavIconWrapper = styled.div`
    width: 64px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

export const PlayIconWrapper = styled.div`
    height: 25px;
    width: 23px;
    border: solid;
    border-color: ${Colors.extra.black};
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

export const IconStyle = {
    marginRight: '16px'
};

export const Menu = styled.div<{ isOpen: boolean; width?: number }>`
    position: absolute;
    right: 10px;
    top: 65px;
    background-color: ${Colors.dropdown.dropdown_bg};
    border-radius: 4px;
    align-items: flex-start;
    padding: 13px 15px;
    gap: 10px;
    display: ${(props) => (props.isOpen ? 'flex' : 'none')};
    flex-direction: column;
    min-width: ${(props) => props.width}px;
    z-index: 1;
    box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19);
`;

export const SettingMenu = styled.div<{ isOpen: boolean; width?: number }>`
    left: 65px;
    position: absolute;
    top: 0;
    background-color: ${Colors.dropdown.dropdown_bg};
    border-radius: 4px;
    align-items: flex-start;
    padding: 13px 15px;
    gap: 10px;
    display: ${(props) => (props.isOpen ? 'flex' : 'none')};
    flex-direction: column;
    min-width: ${(props) => props.width}px;
    z-index: 1;
    box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19);
`;

export const Item = styled.div`
    height: 29px;
    background-color: ${Colors.dropdown.menu_item_bg};
    border-radius: 100px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 15px;
`;

export const EmptyView = styled.div`
    display: flex;
    flex: 1;
    width: 64px;
`;

export const SettingsSidebar = styled.div<{ isOpen: boolean }>`
    width: ${props => props.isOpen ? '280px' : '0px'};
    height: 100%;
    background-color: ${Colors.theme.secondary};
    border-right: 1px solid ${Colors.extra.white}20;
    display: flex;
    flex-direction: column;
    position: relative;
    z-index: 10;
    transition: width 0.3s ease;
    overflow: hidden;
`;

export const SettingsHeader = styled.div`
    padding: 20px 16px 16px 16px;
    border-bottom: 1px solid ${Colors.extra.white}20;
    display: flex;
    align-items: center;
    gap: 12px;
    min-height: 64px;
`;

export const SettingsContent = styled.div`
    flex: 1;
    padding: 16px 0;
    overflow-y: auto;
`;

export const SettingsItem = styled.div`
    padding: 12px 16px;
    cursor: pointer;
    transition: background-color 0.2s ease;
    border-left: 3px solid transparent;
    
    &:hover {
        background-color: ${Colors.extra.white}10;
        border-left-color: ${Colors.theme.primary};
    }
    
    &:active {
        background-color: ${Colors.extra.white}20;
    }
`;
